"use client";

import type { Article } from "@/app/types/blogTypes";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, EyeIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

type ArticleProps = {
  article: Article;
};

export default function BlogHeaderCard({ article }: ArticleProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 30 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      key={article.id}
    >
      <Link href={`/blog/${article.slug}`} className="inline-block w-full">
        <div className="flex flex-col md:flex-row justify-center items-center gap-8 max-w-[1200px] mx-auto px-6 ">
          <motion.div
            className="relative w-full max-w-[1200px] h-[600px] flex aspect-video overflow-hidden"
            whileTap={{ scale: 0.95 }}
            whileHover={{
              scale: 1.03,
              transition: { duration: 0.3, ease: "easeInOut" },
            }}
          >
            {article.cover?.formats?.large.url && (
              <Image
                src={`${article.cover?.formats?.large.url}`}
                alt={`${article.cover?.alternativeText}`}
                width={1200}
                height={600}
                priority
                className="object-cover rounded-[32px] w-full h-full"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />
            )}

            <div className="absolute bottom-0 left-0 w-full h-full bg-gradient-to-t from-[#000000c8]  to-transparent z-10 rounded-b-[32px]"></div>
            <div className="absolute items-start justify-end flex-col bottom-0 left-0 w-full h-1/2 z-20 flex p-6 rounded-b-[32px]">
              {article?.tags?.map((tag) => {
                return (
                  <div
                    key={tag.id}
                    className={`rounded-full mb-2 items-center gap-1 flex justify-center text-xs py-1 px-2 font-bold`}
                    style={{
                      backgroundColor: `${tag?.color?.background}`,
                      color: `${tag?.color?.text}`,
                    }}
                  >
                    {tag?.icon && (
                      <Image
                        src={tag.icon[0].url}
                        alt=""
                        width={12}
                        height={12}
                      />
                    )}
                    {tag?.name}
                  </div>
                );
              })}

              <h1 className="w-full text-white font-lexend  md:w-[70%] text-[28px] leading-[36px] md:text-[40px] md:leading-[48px] font-normal underline-offset-auto">
                {article?.title}
              </h1>
              <div className="flex gap-2 items-center mt-2 md:mt-1">
                <div className="flex items-center gap-2">
                  <Calendar size={20} color="#fff" />
                  <p className="text-white items-center text-sm md:text-base ">
                    {new Date(article.publishedAt).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <span className="text-white">|</span>
                <div className="flex items-center gap-2">
                  <EyeIcon size={20} color="#fff" />
                  <p className="text-white items-center text-sm md:text-base">
                    {article.readingTimeNew} min de leitura
                  </p>
                </div>
              </div>
              <div className="flex mt-8 items-center gap-1">
                <span className="text-white ">Leia mais sobre</span>
                <ArrowRight size={20} color="#fff" />
              </div>
            </div>
          </motion.div>
        </div>
      </Link>
    </motion.div>
  );
}
